import React, { useState } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Space,
} from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Case, CaseStatus, updateCase, updateCaseDetail } from '../../../../services/case';

const { TextArea } = Input;

interface CaseBasicInfoProps {
  caseData: Case;
  canEdit: boolean;
  onRefresh: () => void;
}

const CaseBasicInfo: React.FC<CaseBasicInfoProps> = ({ caseData, canEdit, onRefresh }) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // Get status text
  const getStatusText = (status: CaseStatus): string => {
    const textMap: Record<CaseStatus, string> = {
      [CaseStatus.ACTIVE]: '进行中',
      [CaseStatus.CLOSED]: '已结案',
      [CaseStatus.SUSPENDED]: '已暂停',
      [CaseStatus.PENDING]: '待处理',
      [CaseStatus.IN_PROGRESS]: '进行中',
      [CaseStatus.WAITING_CLIENT]: '等待客户',
      [CaseStatus.WAITING_COURT]: '等待法院',
      [CaseStatus.COMPLETED]: '已完成',
      [CaseStatus.CANCELLED]: '已取消',
    };
    return textMap[status] || status;
  };

  // Handle edit
  const handleEdit = () => {
    form.setFieldsValue({
      case_name: caseData.case_name,
      case_type: caseData.case_type,
      case_level: caseData.details?.case_level,
      case_reason: caseData.details?.case_reason,
      case_status: caseData.case_status,
      accept_date: caseData.accept_date && dayjs(caseData.accept_date).isValid() ? dayjs(caseData.accept_date) : null,
      court_date: caseData.court_date && dayjs(caseData.court_date).isValid() ? dayjs(caseData.court_date) : null,
      verdict_date: caseData.verdict_date && dayjs(caseData.verdict_date).isValid() ? dayjs(caseData.verdict_date) : null,
      court_name: caseData.details?.court_name,
      court_contact: caseData.details?.court_contact,
      case_summary: caseData.details?.case_summary,
      notes: caseData.details?.notes,
    });
    setEditMode(true);
  };

  // Handle cancel
  const handleCancel = () => {
    form.resetFields();
    setEditMode(false);
  };

  // Handle save
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Update basic case info
      const updateData: any = {
        case_name: values.case_name,
        case_type: values.case_type,
        case_status: values.case_status,
        accept_date: values.accept_date ? values.accept_date.format('YYYY-MM-DD') : undefined,
        court_date: values.court_date ? values.court_date.format('YYYY-MM-DD HH:mm:ss') : undefined,
        verdict_date: values.verdict_date ? values.verdict_date.format('YYYY-MM-DD') : undefined,
      };

      await updateCase(caseData.id, updateData);

      // Update case details
      const detailData = {
        case_level: values.case_level,
        case_reason: values.case_reason,
        court_name: values.court_name,
        court_contact: values.court_contact,
        case_summary: values.case_summary,
        notes: values.notes,
      };

      await updateCaseDetail(caseData.id, detailData);

      message.success('保存成功');
      setEditMode(false);
      onRefresh();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('请检查表单填写');
      } else {
        message.error('保存失败');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="基本信息"
      extra={
        canEdit && !editMode && (
          <Button type="link" icon={<EditOutlined />} onClick={handleEdit}>
            编辑
          </Button>
        )
      }
    >
      {editMode ? (
        <Form form={form} layout="vertical">
          <Form.Item
            label="案件名称"
            name="case_name"
            rules={[{ required: true, message: '请输入案件名称' }]}
          >
            <Input placeholder="请输入案件名称" />
          </Form.Item>

          <Form.Item
            label="案件类型"
            name="case_type"
          >
            <Select placeholder="请选择案件类型">
              <Select.Option value="民事">民事</Select.Option>
              <Select.Option value="刑事">刑事</Select.Option>
              <Select.Option value="行政">行政</Select.Option>
              <Select.Option value="商事">商事</Select.Option>
              <Select.Option value="劳动">劳动</Select.Option>
              <Select.Option value="知识产权">知识产权</Select.Option>
              <Select.Option value="其他">其他</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label="审级" 
            name="case_level"
          >
            <Select placeholder="请选择审级">
              <Select.Option value="一审">一审</Select.Option>
              <Select.Option value="二审">二审</Select.Option>
              <Select.Option value="再审">再审</Select.Option>
              <Select.Option value="执行">执行</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label="案由" 
            name="case_reason"
          >
            <Input placeholder="请输入案由" />
          </Form.Item>

          <Form.Item label="案件状态" name="case_status">
            <Select placeholder="请选择案件状态">
              <Select.Option value={CaseStatus.ACTIVE}>进行中</Select.Option>
              <Select.Option value={CaseStatus.CLOSED}>已结案</Select.Option>
              <Select.Option value={CaseStatus.SUSPENDED}>已暂停</Select.Option>
              <Select.Option value={CaseStatus.PENDING}>待处理</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="承接日期" name="accept_date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item 
            label="开庭时间" 
            name="court_date"
          >
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          <Form.Item 
            label="判决日期" 
            name="verdict_date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item 
            label="法院名称" 
            name="court_name"
          >
            <Input placeholder="请输入法院名称" />
          </Form.Item>

          <Form.Item label="法院联系方式" name="court_contact">
            <Input placeholder="请输入法院联系方式" />
          </Form.Item>

          <Form.Item label="案件描述" name="case_summary">
            <TextArea
              rows={4}
              placeholder="请输入案件描述"
              showCount
              maxLength={2000}
            />
          </Form.Item>

          <Form.Item label="备注" name="notes">
            <TextArea
              rows={3}
              placeholder="请输入备注"
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
              >
                保存
              </Button>
              <Button icon={<CloseOutlined />} onClick={handleCancel}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      ) : (
        <Descriptions 
          column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }} 
          bordered
        >
          <Descriptions.Item label="案件编号">{caseData.case_number}</Descriptions.Item>
          <Descriptions.Item label="案件名称">{caseData.case_name}</Descriptions.Item>
          <Descriptions.Item label="案件类型">{caseData.case_type}</Descriptions.Item>
          <Descriptions.Item label="审级">{caseData.details?.case_level || '-'}</Descriptions.Item>
          <Descriptions.Item label="案由" span={2}>
            {caseData.details?.case_reason || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="案件状态">
            {getStatusText(caseData.case_status)}
          </Descriptions.Item>
          <Descriptions.Item label="承接日期">
            {caseData.accept_date ? dayjs(caseData.accept_date).format('YYYY-MM-DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="开庭时间">
            {caseData.court_date ? dayjs(caseData.court_date).format('YYYY-MM-DD HH:mm') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="判决日期">
            {caseData.verdict_date ? dayjs(caseData.verdict_date).format('YYYY-MM-DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="法院名称">
            {caseData.details?.court_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="法院联系方式">
            {caseData.details?.court_contact || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="负责律师">
            {caseData.responsible_lawyer_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="助理律师">
            {caseData.assistant_lawyer_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="创建人">
            {caseData.created_by_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {caseData.created_at ? dayjs(caseData.created_at).format('YYYY-MM-DD HH:mm') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="案件描述" span={2}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {caseData.details?.case_summary || '-'}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="备注" span={2}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {caseData.details?.notes || '-'}
            </div>
          </Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
};

export default CaseBasicInfo;
